import { FC, useEffect, useMemo, useState } from 'react';

import { sortEmployerUsers } from 'utils/clickme';
import { AccountCreateError } from 'utils/constants/accounts';

import accountCreateNameFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_create/account_create_name_form_field_touch';
import accountCreateSaveFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_create/account_create_save_form_submit';
import accountEditNameFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_edit/account_edit_name_form_field_touch';
import accountEditSaveFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_edit/account_edit_save_form_submit';
import { AdditionalDefault, Button, createStaticDataProvider, fromTree, Input } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { AccountUsersInput } from 'components/AccountCreate/components/AccountForm/components/AccountUsersInput';
import { ContractFields } from 'components/AccountCreate/components/AccountForm/components/ContractFields';
import { AccountFormItem } from 'components/AccountCreate/components/AccountFormItem';
import { createAccount } from 'components/AccountCreate/createAccount';
import CompositeSelection from 'components/common/CompositeSelection/CompositeSelection';

import {
    setAccountsAccountTitle,
    setAccountsAccountUsers,
    setAccountsEmployerUsers,
    setCreateAccountErrors,
} from 'store/models/accounts';
import { useDispatch, useSelector } from 'store/utils';

export const TITLE_ERRORS_TRL = {
    [AccountCreateError.EmptyTitle]: 'account.create.confirmation.error.emptyTitle',
    [AccountCreateError.LongTitle]: 'account.create.confirmation.error.longTitle',
    [AccountCreateError.DuplicateTitle]: 'account.create.confirmation.error.duplicateTitle',
    [AccountCreateError.InvalidExpiredDateAndPrisePerClick]:
        'account.create.confirmation.error.invalidExpiredDateAndPrisePerClick',
};

interface AccountFormProps {
    setConfirmationModalVisible: (isConfirmationModalVisible: boolean) => void;
}

export const AccountForm: FC<AccountFormProps> = ({ setConfirmationModalVisible }) => {
    const { employerUsers, accountUsers, accountTitle, accountId, accountErrors } = useSelector(
        (state) => state.accounts
    );
    const [accountTitleError, setAccountTitleError] = useState<AccountCreateError | null>(null);
    const [accountContractError, setAccountContractError] = useState<AccountCreateError | null>(null);
    const [initialAccountTitle, setInitialAccountTitle] = useState('');
    const [initialAccountUsersCount, setInitialAccountUsersCount] = useState(0);

    const dispatch = useDispatch();

    const trl = useTrls();

    const data = useMemo(() => {
        const modelData = employerUsers.map(({ id, name, email }) => ({
            id: id.toString(),
            text: `${name} (${email})`,
            items: undefined,
        }));
        const collection = fromTree<AdditionalDefault>(modelData);

        return {
            collection,
            provider: createStaticDataProvider(
                employerUsers.map(({ id, name, email }) => ({
                    value: id.toString(),
                    text: `${name} (${email})`,
                }))
            ),
        };
    }, [employerUsers]);

    useEffect(() => {
        accountErrors.forEach((errorKey) => {
            if (errorKey === AccountCreateError.InvalidExpiredDateAndPrisePerClick) {
                setAccountContractError(errorKey);
                return;
            }
            if (
                [
                    AccountCreateError.LongTitle,
                    AccountCreateError.EmptyTitle,
                    AccountCreateError.DuplicateTitle,
                ].includes(errorKey)
            ) {
                setAccountTitleError(errorKey);
            }
        });
    }, [accountErrors]);

    useEffect(() => {
        setInitialAccountTitle(accountTitle);
        setInitialAccountUsersCount(accountUsers.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <AccountFormItem
                element={
                    <Input
                        value={accountTitle ?? ''}
                        onFocus={() => {
                            if (accountId) {
                                accountEditNameFormFieldTouch();
                            } else {
                                accountCreateNameFormFieldTouch();
                            }
                        }}
                        onChange={(value) => {
                            dispatch(setAccountsAccountTitle(value));
                            if (accountTitleError) {
                                dispatch(
                                    setCreateAccountErrors(accountErrors.filter((key) => key !== accountTitleError))
                                );
                                setAccountTitleError(null);
                            }
                        }}
                        placeholder={trl('account.create.name.placeholder')}
                        invalid={!!accountTitleError}
                        data-qa="cabinet-name-input"
                    />
                }
                label={trl('account.create.name.legend')}
                hint={trl('account.create.name.hint')}
                error={accountTitleError ? trl(TITLE_ERRORS_TRL[accountTitleError]) : null}
                isRequired
            />
            <AccountFormItem
                element={
                    <CompositeSelection.Root
                        collection={data.collection}
                        value={accountUsers}
                        onChange={(users) => {
                            dispatch([
                                setAccountsAccountUsers(users),
                                setAccountsEmployerUsers(sortEmployerUsers(employerUsers, users)),
                            ]);
                        }}
                    >
                        <AccountUsersInput provider={data.provider} accountId={accountId} />
                        <CompositeSelection.TreeSelectorModal
                            texts={{
                                title: trl('account.create.modal.users.title'),
                                submit: trl('account.create.modal.users.add'),
                                cancel: trl('account.create.modal.users.cancel'),
                                searchPlaceholder: trl('account.create.modal.users.placeholder'),
                                notFound: trl('account.create.modal.users.notFound'),
                            }}
                        />
                    </CompositeSelection.Root>
                }
                label={trl('account.create.users.legend')}
                hint={accountUsers.length === 0 ? trl('account.create.users.hint') : undefined}
            />
            <ContractFields
                accountContractError={accountContractError}
                setAccountContractError={setAccountContractError}
            />
            <AccountFormItem
                element={
                    <Button
                        mode="primary"
                        style="accent"
                        onClick={() => {
                            if (accountId) {
                                accountEditSaveFormSubmit({
                                    isNameChanged: initialAccountTitle !== accountTitle,
                                    isEmployeeCountChanged: initialAccountUsersCount !== accountUsers.length,
                                });
                            } else {
                                accountCreateSaveFormSubmit({ employeeCount: accountUsers.length });
                            }
                            void dispatch(createAccount(setConfirmationModalVisible, !accountId));
                        }}
                        data-qa={accountId ? 'save-changes' : 'create-cabinet'}
                    >
                        {trl(accountId ? 'account.edit.save' : 'account.create.create')}
                    </Button>
                }
            />
        </>
    );
};
